import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { useToast } from "../../../../contexts/ToastContext";
import { useLoadState } from "../../../../hooks/useLoadState";
import Button from "../../inputs/Button";
import InputField from "../../inputs/InputField";
import DialogTemplate from "../../../layout/DialogTemplate";
import { zugriffApi } from "../../../../services/api/zugriff";
import {
  BereichKey,
  BereichOption,
  ExterneFreigabe,
  Modus,
  ZugriffUebersicht,
} from "../../../../types/zugriff";
import {
  alsFreigabeliste,
  chipsFuer,
  freigabeStatusVisual,
  OberpunktChip,
} from "../../../../utils/zugriff";
import { colors } from "../../../../styles/theme";
import BaseTable, {
  TableColumn,
  TableData,
} from "../../../tables/common/BaseTable";
import StatusChip from "../../display/StatusChip";
import BereichBaum from "./BereichBaum";

interface ZugriffDialogProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onClose: () => void;
}

type Ansicht = { art: "liste" } | { art: "neu" };

const LEERE_EINGABE = { email: "", vorname: "", nachname: "", firma: "" };

// Die Breiten lassen die Aktionsspalte (AKTIONEN_BREITE) frei – zusammen ergeben sie 100 %.
// Andernfalls schöbe die von BaseTable ergänzte Spalte die Tabelle über den Dialog hinaus.
const AKTIONEN_BREITE = "14%";

const GAST_SPALTEN: TableColumn[] = [
  { key: "name", label: "Name", align: "left", width: "22%", sortable: true },
  {
    key: "email",
    label: "E-Mail",
    align: "left",
    width: "30%",
    sortable: true,
  },
  {
    key: "status",
    label: "Status",
    align: "left",
    width: "16%",
    sortable: true,
  },
  { key: "bereiche", label: "Bereiche", align: "left", width: "18%" },
];

const INTERN_SPALTEN: TableColumn[] = [
  { key: "name", label: "Name", align: "left", width: "50%", sortable: true },
  {
    key: "email",
    label: "E-Mail",
    align: "left",
    width: "50%",
    sortable: true,
  },
];

/**
 * Zugriffsverwaltung eines Projekts – ein Dialog mit zwei Ansichten.
 *
 * Zuerst die Liste der Berechtigten: die Mitglieder der eigenen Organisation (Zugriff über die
 * Rolle, hier nicht steuerbar) und die Gäste (Zugriff über Freigaben). Von dort führt „Gast hinzufügen" an derselben Stelle zur Eingabemaske mit dem
 * Bereichsbaum – kein zweiter Dialog darüber, damit es nur ein Abbrechen und ein Speichern gibt.
 */
const ZugriffDialog: React.FC<ZugriffDialogProps> = ({
  projectId,
  projectName,
  open,
  onClose,
}) => {
  const { showSuccess, showError } = useToast();

  const [katalog, setKatalog] = useState<BereichOption[]>([]);
  const [uebersicht, setUebersicht] = useState<ZugriffUebersicht | null>(null);
  const {
    loading: laedt,
    ladenStarten,
    ladenBeenden,
  } = useLoadState(projectId);
  const [ansicht, setAnsicht] = useState<Ansicht>({ art: "liste" });
  const [eingabe, setEingabe] = useState(LEERE_EINGABE);
  const [auswahl, setAuswahl] = useState<Map<BereichKey, Modus>>(new Map());
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    ladenStarten();
    try {
      const [katalogAntwort, uebersichtAntwort] = await Promise.all([
        zugriffApi.getBereichOptions(),
        zugriffApi.getZugriff(projectId),
      ]);
      setKatalog(katalogAntwort.bereiche);
      setUebersicht(uebersichtAntwort);
    } catch {
      showError("Die Zugriffsliste konnte nicht geladen werden");
    } finally {
      ladenBeenden();
    }
  }, [projectId, showError, ladenStarten, ladenBeenden]);

  useEffect(() => {
    if (!open) return;
    setAnsicht({ art: "liste" });
    setFehler(null);
    laden();
  }, [open, laden]);

  const zurListe = () => {
    setAnsicht({ art: "liste" });
    setEingabe(LEERE_EINGABE);
    setAuswahl(new Map());
    setFehler(null);
  };

  const neuBeginnen = () => {
    setEingabe(LEERE_EINGABE);
    setAuswahl(new Map());
    setFehler(null);
    setAnsicht({ art: "neu" });
  };

  const speichern = async () => {
    if (auswahl.size === 0) {
      setFehler("Mindestens ein Bereich muss freigegeben werden");
      return;
    }
    setSpeichert(true);
    setFehler(null);
    try {
      const ergebnis = await zugriffApi.freigeben(projectId, {
        ...eingabe,
        bereiche: alsFreigabeliste(auswahl),
      });
      if (ergebnis.einladung_versendet) {
        showSuccess(`${eingabe.email} wurde eingeladen und freigegeben`);
      } else {
        showSuccess("Freigabe eingerichtet");
        setFehler(ergebnis.hinweis);
      }
      await laden();
      setAnsicht({ art: "liste" });
    } catch (e) {
      setFehler(
        e instanceof Error
          ? e.message
          : "Die Freigabe konnte nicht gespeichert werden",
      );
    } finally {
      setSpeichert(false);
    }
  };

  const entziehen = async (row: TableData) => {
    try {
      await zugriffApi.entziehen(projectId, row.id);
      showSuccess(`Zugriff für ${row.name} entzogen`);
      await laden();
    } catch {
      showError("Der Zugriff konnte nicht entzogen werden");
    }
  };

  const internZeilen = (uebersicht?.intern ?? []).map((nutzer) => ({
    id: nutzer.user_id,
    name: nutzer.name,
    email: nutzer.email,
  }));

  const gastZeilen = (uebersicht?.extern ?? []).map((freigabe) => ({
    id: freigabe.subjekt,
    name: freigabe.name || freigabe.email,
    email: freigabe.email,
    status: freigabeStatusVisual(freigabe.ist_eingeladen).label,
    bereiche: chipsFuer(freigabe.bereiche, katalog),
    freigabe,
  }));

  /**
   * Was in der Zeile keinen Platz hat, steht im Tooltip: die Herkunft der Freigabe am Namen,
   * die vollständige Bereichsliste an den Chips. Die Bereiche bleiben dafür einzeilig – sonst
   * wüchse die Zeilenhöhe mit der Zahl der Bereiche.
   */
  const gastZelle = (column: TableColumn, row: TableData, wert: unknown) => {
    const freigabe = row.freigabe as ExterneFreigabe;

    if (column.key === "name") {
      return (
        <Tooltip title={herkunft(freigabe)}>
          <span>{row.name}</span>
        </Tooltip>
      );
    }

    if (column.key === "status") {
      return <StatusChip {...freigabeStatusVisual(freigabe.ist_eingeladen)} />;
    }

    if (column.key === "bereiche") {
      const chips = row.bereiche as OberpunktChip[];
      return (
        <Tooltip
          title={
            <Box>
              {chips.map((chip) => (
                <Box key={chip.label}>{beschriftung(chip)}</Box>
              ))}
            </Box>
          }
        >
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              flexWrap: "nowrap",
              overflow: "hidden",
            }}
          >
            {chips.map((chip) => (
              <Chip key={chip.label} size="small" label={beschriftung(chip)} />
            ))}
          </Box>
        </Tooltip>
      );
    }

    return wert as React.ReactNode;
  };

  const darfAendern = uebersicht?.darf_aendern ?? false;
  const eingabeVollstaendig =
    eingabe.email.trim() &&
    eingabe.vorname.trim() &&
    eingabe.nachname.trim() &&
    eingabe.firma.trim();

  const titel = ansicht.art === "neu" ? "Gast hinzufügen" : "Zugriff verwalten";

  return (
    <DialogTemplate
      open={open}
      onClose={onClose}
      title={`${titel} · ${projectName}`}
      headerLeft={
        ansicht.art === "liste" ? undefined : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              color: colors.text.secondary,
            }}
            onClick={zurListe}
            role="button"
            aria-label="Zurück zur Liste"
          >
            <IconButton size="small" component="span">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2">Zurück zur Liste</Typography>
          </Box>
        )
      }
      maxWidth="md"
      actions={
        ansicht.art === "liste" ? (
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={zurListe} disabled={speichert}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={speichern}
              disabled={speichert || !eingabeVollstaendig}
            >
              Einladen und freigeben
            </Button>
          </>
        )
      }
    >
      <Box>
        {laedt ? null : ansicht.art === "liste" ? (
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {uebersicht?.mandant}
              </Typography>
              <BaseTable
                columns={INTERN_SPALTEN}
                data={internZeilen}
                fixedLayout
                minWidth={0}
                disablePagination
                emptyMessage="Dieser Organisation ist bisher niemand zugeordnet."
              />
            </Box>

            <Divider />

            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  Gäste
                </Typography>
                {darfAendern && (
                  <Button
                    variant="secondary"
                    startIcon={<PersonAddIcon />}
                    onClick={neuBeginnen}
                  >
                    Gast hinzufügen
                  </Button>
                )}
              </Box>

              <BaseTable
                columns={GAST_SPALTEN}
                data={gastZeilen}
                renderCell={gastZelle}
                fixedLayout
                minWidth={0}
                actionsColumnWidth={AKTIONEN_BREITE}
                disablePagination
                onDelete={darfAendern ? (row) => entziehen(row) : undefined}
                confirmDelete={darfAendern}
                confirmDeleteMessage={(row) =>
                  `Zugriff für ${row.name} wirklich entziehen?`
                }
                emptyMessage="Für dieses Projekt ist bisher kein Gast freigegeben."
              />
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {fehler && <Alert severity="warning">{fehler}</Alert>}

            {ansicht.art === "neu" && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Gast
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 2,
                  }}
                >
                  <InputField
                    label="Vorname"
                    value={eingabe.vorname}
                    onChange={(wert) =>
                      setEingabe({ ...eingabe, vorname: wert })
                    }
                    required
                  />
                  <InputField
                    label="Nachname"
                    value={eingabe.nachname}
                    onChange={(wert) =>
                      setEingabe({ ...eingabe, nachname: wert })
                    }
                    required
                  />
                  <InputField
                    label="E-Mail"
                    value={eingabe.email}
                    onChange={(wert) => setEingabe({ ...eingabe, email: wert })}
                    required
                  />
                  <InputField
                    label="Firma"
                    value={eingabe.firma}
                    onChange={(wert) => setEingabe({ ...eingabe, firma: wert })}
                    required
                  />
                </Box>
              </Box>
            )}

            {ansicht.art === "neu" && <Divider />}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Freigegebene Bereiche
              </Typography>
              <BereichBaum
                katalog={katalog}
                auswahl={auswahl}
                onChange={setAuswahl}
                disabled={speichert}
              />
              <Typography
                variant="caption"
                sx={{ color: colors.text.secondary }}
              >
                Ein Häkchen bedeutet lesenden Zugriff. Die Projektübersicht ist
                immer enthalten.
              </Typography>
            </Box>
          </Stack>
        )}
      </Box>
    </DialogTemplate>
  );
};

/** Bereichs-Chip: „teilweise" für einzelne Unterpunkte, „✎" für Schreibrecht. */
const beschriftung = (chip: OberpunktChip) =>
  `${chip.label}${chip.teilweise ? " (teilweise)" : ""}${chip.schreibend ? " ✎" : ""}`;

/** Woher die Freigabe stammt – Firma des Gasts, wer sie erteilt hat und wann. */
const herkunft = (freigabe: ExterneFreigabe) => (
  <Box>
    {freigabe.firma && <Box>{freigabe.firma}</Box>}
    {freigabe.erstellt_von && (
      <Box>
        Freigegeben von {freigabe.erstellt_von} am{" "}
        {new Date(freigabe.erstellt_am).toLocaleDateString("de-DE")}
      </Box>
    )}
  </Box>
);

export default ZugriffDialog;
